// components/Workers/WorkerForm.jsx
import React, { useState, useEffect, useCallback } from 'react';
import {
  Box,
  Card,
  CardContent,
  Typography,
  Button,
  Grid,
  TextField,
  MenuItem,
  FormControl,
  InputLabel,
  Select,
  Stepper,
  Step,
  StepLabel,
  IconButton,
  Alert,
  Chip
} from '@mui/material';
import {
  PhotoCamera,
  Upload as UploadIcon
} from '@mui/icons-material';
import { DatePicker } from '@mui/x-date-pickers/DatePicker';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { AdapterDayjs } from '@mui/x-date-pickers/AdapterDayjs';
import axios from 'axios';
import dayjs from 'dayjs';

const WorkerForm = ({ workerId, onSave, onCancel }) => {
  const [activeStep, setActiveStep] = useState(0);
  const [loading, setLoading] = useState(false);
  const [contractors, setContractors] = useState([]);
  const [uploadFiles, setUploadFiles] = useState({});

  const steps = [
    'Personal Details',
    'Identification',
    'Medical Details',
    'Employment',
    'Banking Details',
    'Documents'
  ];

  const [formData, setFormData] = useState({
    // Personal Details Tab
    personalDetails: {
      firstName: '',
      lastName: '',
      dateOfBirth: null,
      gender: '',
      maritalStatus: '',
      bloodGroup: '',
      phoneNumber: '',
      alternatePhone: '',
      emailAddress: '',
      education: {
        highestQualification: '',
        instituteName: '',
        yearOfPassing: '',
        percentage: ''
      },
      physicalDetails: {
        height: '',
        weight: '',
        identificationMarks: [],
        disabilities: []
      },
      emergencyContact: {
        name: '',
        relationship: '',
        phone: '',
        address: ''
      }
    },

    // Identification Details Tab
    identificationDetails: {
      pfNumber: '',
      esicNumber: '',
      panNumber: '',
      aadhaarNumber: '',
      validityDetails: {
        pfValidTill: null,
        esicValidTill: null,
        medicalValidTill: null
      }
    },

    // Medical Details Tab
    medicalDetails: {
      preMedicalDate: null,
      medicalStatus: 'pending',
      allergies: [],
      chronicConditions: [],
      vaccinations: []
    },

    // Employment Details Tab
    employmentDetails: {
      contractorId: '',
      designation: '',
      department: '',
      joiningDate: null,
      contractEndDate: null,
      employmentType: 'contract',
      workLocation: '',
      skills: [],
      experience: '',
      experienceYears: '',
      qualification: '',
      photoPath: '',
      thumbImpression: '',
      currentStatus: 'active',
      terminationDate: null,
      terminationReason: '',
      medicalFitnessDate: null,
      shiftTimings: { start: '', end: '' },
      salary: {
        basicSalary: '',
        hra: '',
        conveyance: '',
        medical: '',
        allowances: { others: '' }
      }
    },

    // Banking Details Tab
    bankingDetails: {
      primaryAccount: {
        accountNumber: '',
        bankName: '',
        branchName: '',
        ifscCode: '',
        accountType: 'savings',
        accountHolderName: ''
      },
      nominee: { name: '', relationship: '' },
      digitalPayments: {
        upiId: '',
        preferredUpiApp: ''
      }
    }
  });

  const fetchContractors = useCallback(async () => {
    try {
      const response = await axios.get('/api/contractors', {
        headers: { 
          Authorization: `Bearer ${localStorage.getItem('token')}`,
          "x-tenant-id": localStorage.getItem("tenantId"),
        }
      });
      // Handle the API response structure
      if (response.data.success) {
        setContractors(response.data.data.contractors || []);
      } else {
        setContractors([]);
      }
    } catch (error) {
      console.error('Error fetching contractors:', error);
    }
  }, []);

  const fetchWorkerDetails = useCallback(async () => {
    if (!workerId) return;
    
    try {
      setLoading(true);
      const response = await axios.get(`/api/worker-details/${workerId}`, {
        headers: { 
          Authorization: `Bearer ${localStorage.getItem('token')}`,
          "x-tenant-id": localStorage.getItem("tenantId"),
        }
      });
      
      // Handle different response structures
      const workerData = response.data?.data?.worker || response.data?.worker || response.data?.data || response.data;
      
      if (workerData) {
        // Map the API response to form data structure
        setFormData({
          personalDetails: {
            firstName: workerData.personalDetails?.firstName || workerData.firstName || '',
            lastName: workerData.personalDetails?.lastName || workerData.lastName || '',
            dateOfBirth: workerData.personalDetails?.dateOfBirth 
              ? dayjs(workerData.personalDetails.dateOfBirth) 
              : workerData.dateOfBirth 
                ? dayjs(workerData.dateOfBirth) 
                : null,
            gender: workerData.personalDetails?.gender || workerData.gender || '',
            maritalStatus: workerData.personalDetails?.maritalStatus || workerData.maritalStatus || '',
            bloodGroup: workerData.personalDetails?.bloodGroup || workerData.bloodGroup || '',
            phoneNumber: workerData.personalDetails?.phoneNumber || workerData.phoneNumber || '',
            alternatePhone: workerData.personalDetails?.alternatePhone || workerData.alternatePhone || '',
            emailAddress: workerData.personalDetails?.emailAddress || workerData.emailAddress || '',
            education: workerData.personalDetails?.education || workerData.education || {
              highestQualification: '',
              instituteName: '',
              yearOfPassing: '',
              percentage: ''
            },
            physicalDetails: workerData.personalDetails?.physicalDetails || workerData.physicalDetails || {
              height: '',
              weight: '',
              identificationMarks: [],
              disabilities: []
            },
            emergencyContact: workerData.personalDetails?.emergencyContact || workerData.emergencyContact || {
              name: '',
              relationship: '',
              phone: '',
              address: ''
            }
          },
          identificationDetails: {
            pfNumber: workerData.identificationDetails?.pfNumber || workerData.pfNumber || '',
            esicNumber: workerData.identificationDetails?.esicNumber || workerData.esicNumber || '',
            panNumber: workerData.identificationDetails?.panNumber || workerData.panNumber || '',
            aadhaarNumber: workerData.identificationDetails?.aadhaarNumber || workerData.aadhaarNumber || '',
            validityDetails: {
              pfValidTill: workerData.identificationDetails?.validityDetails?.pfValidTill 
                ? dayjs(workerData.identificationDetails.validityDetails.pfValidTill)
                : workerData.pfValidTill ? dayjs(workerData.pfValidTill) : null,
              esicValidTill: workerData.identificationDetails?.validityDetails?.esicValidTill
                ? dayjs(workerData.identificationDetails.validityDetails.esicValidTill)
                : workerData.esicValidTill ? dayjs(workerData.esicValidTill) : null,
              medicalValidTill: workerData.identificationDetails?.validityDetails?.medicalValidTill
                ? dayjs(workerData.identificationDetails.validityDetails.medicalValidTill)
                : workerData.medicalValidTill ? dayjs(workerData.medicalValidTill) : null,
            }
          },
          medicalDetails: {
            preMedicalDate: workerData.medicalDetails?.preMedicalDate
              ? dayjs(workerData.medicalDetails.preMedicalDate)
              : workerData.preMedicalDate ? dayjs(workerData.preMedicalDate) : null,
            medicalStatus: workerData.medicalDetails?.medicalStatus || workerData.medicalStatus || 'pending',
            allergies: workerData.medicalDetails?.allergies || workerData.allergies || [],
            chronicConditions: workerData.medicalDetails?.chronicConditions || workerData.chronicConditions || [],
            vaccinations: workerData.medicalDetails?.vaccinations || workerData.vaccinations || []
          },
          employmentDetails: {
            contractorId: workerData.employmentDetails?.contractorId || workerData.contractorId || '',
            designation: workerData.employmentDetails?.designation || workerData.designation || '',
            joiningDate: workerData.employmentDetails?.joiningDate
              ? dayjs(workerData.employmentDetails.joiningDate)
              : workerData.joiningDate ? dayjs(workerData.joiningDate) : null,
            workLocation: workerData.employmentDetails?.workLocation || workerData.workLocation || '',
            shift: workerData.employmentDetails?.shift || workerData.shift || '',
            status: workerData.employmentDetails?.status || workerData.status || 'active'
          },
          bankingDetails: {
            primaryAccount: workerData.bankingDetails?.primaryAccount || workerData.primaryAccount || {
              accountNumber: '',
              bankName: '',
              ifscCode: '',
              branchName: '',
              accountHolderName: ''
            },
            secondaryAccount: workerData.bankingDetails?.secondaryAccount || workerData.secondaryAccount || {
              accountNumber: '',
              bankName: '',
              ifscCode: '',
              branchName: '',
              accountHolderName: ''
            }
          },
          documents: workerData.documents || {}
        });
      }
    } catch (error) {
      console.error('Error fetching worker details:', error);
    } finally {
      setLoading(false);
    }
  }, [workerId]);

  useEffect(() => {
    fetchContractors();
    if (workerId) {
      fetchWorkerDetails();
    }
  }, [workerId, fetchContractors, fetchWorkerDetails]);

  const handleNext = () => {
    if (validateStep(activeStep)) {
      setActiveStep((prevActiveStep) => prevActiveStep + 1);
    }
  };

  const handleBack = () => {
    setActiveStep((prevActiveStep) => prevActiveStep - 1);
  };

  const validateStep = (step) => {
    switch (step) {
      case 0: // Personal Details
        return formData.personalDetails.firstName && 
               formData.personalDetails.lastName && 
               formData.personalDetails.dateOfBirth &&
               formData.personalDetails.gender &&
               formData.personalDetails.bloodGroup &&
               formData.personalDetails.phoneNumber;
      case 1: // Identification
        return formData.identificationDetails.pfNumber &&
               formData.identificationDetails.esicNumber &&
               formData.identificationDetails.panNumber &&
               formData.identificationDetails.aadhaarNumber;
      case 2: // Medical
        return formData.medicalDetails.preMedicalDate;
      case 3: // Employment
        return formData.employmentDetails.contractorId &&
               formData.employmentDetails.designation &&
               formData.employmentDetails.joiningDate;
      case 4: // Banking
        return formData.bankingDetails.primaryAccount.accountNumber &&
               formData.bankingDetails.primaryAccount.bankName &&
               formData.bankingDetails.primaryAccount.ifscCode;
      default:
        return true;
    }
  };

  const handleSave = async () => {
    try {
      setLoading(true);

      const formDataToSend = new FormData();

      // Append form data
      Object.keys(formData).forEach(key => {
        formDataToSend.append(key, JSON.stringify(formData[key]));
      });

      // Append files
      Object.keys(uploadFiles).forEach(key => {
        if (uploadFiles[key]) {
          formDataToSend.append(key, uploadFiles[key]);
        }
      });

      const url = workerId ? `/api/worker-details/${workerId}` : '/api/worker-details';
      const method = workerId ? 'put' : 'post';

      await axios[method](url, formDataToSend, {
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('token')}`,
          'x-tenant-id': localStorage.getItem("tenantId"),
          'Content-Type': 'multipart/form-data'
        }
      });

      onSave && onSave();
    } catch (error) {
      console.error('Error saving worker:', error);
      alert('Error saving worker: ' + error.response?.data?.message);
    } finally {
      setLoading(false);
    }
  };

  const updateFormData = (section, field, value) => {
    setFormData(prev => ({
      ...prev,
      [section]: {
        ...prev[section],
        [field]: value
      }
    }));
  };

  const updateNestedFormData = (section, subsection, field, value) => {
    setFormData(prev => ({
      ...prev,
      [section]: {
        ...prev[section],
        [subsection]: {
          ...prev[section][subsection],
          [field]: value
        }
      }
    }));
  };

  const handleFileUpload = (fieldName, file) => {
    setUploadFiles(prev => ({
      ...prev,
      [fieldName]: file
    }));
  };

  const renderPersonalDetailsStep = () => (
    <Grid container spacing={3}>
      <Grid item xs={12} sm={6}>
        <TextField
          fullWidth
          label="First Name *"
          value={formData.personalDetails.firstName}
          onChange={(e) => updateFormData('personalDetails', 'firstName', e.target.value)}
          required
        />
      </Grid>
      <Grid item xs={12} sm={6}>
        <TextField
          fullWidth
          label="Last Name *"
          value={formData.personalDetails.lastName}
          onChange={(e) => updateFormData('personalDetails', 'lastName', e.target.value)}
          required
        />
      </Grid>
      <Grid item xs={12} sm={6}>
        <DatePicker
          label="Date of Birth *"
          value={formData.personalDetails.dateOfBirth ? dayjs(formData.personalDetails.dateOfBirth) : null}
          onChange={(date) => updateFormData('personalDetails', 'dateOfBirth', date?.toDate())}
          renderInput={(params) => <TextField {...params} fullWidth required />}
        />
      </Grid>
      <Grid item xs={12} sm={6}>
        <FormControl fullWidth required>
          <InputLabel>Gender *</InputLabel>
          <Select
            value={formData.personalDetails.gender}
            onChange={(e) => updateFormData('personalDetails', 'gender', e.target.value)}
            label="Gender *"
          >
            <MenuItem value="male">Male</MenuItem>
            <MenuItem value="female">Female</MenuItem>
            <MenuItem value="other">Other</MenuItem>
          </Select>
        </FormControl>
      </Grid>
      <Grid item xs={12} sm={6}>
        <FormControl fullWidth required>
          <InputLabel>Marital Status *</InputLabel>
          <Select
            value={formData.personalDetails.maritalStatus}
            onChange={(e) => updateFormData('personalDetails', 'maritalStatus', e.target.value)}
            label="Marital Status *"
          >
            <MenuItem value="single">Single</MenuItem>
            <MenuItem value="married">Married</MenuItem>
            <MenuItem value="divorced">Divorced</MenuItem>
            <MenuItem value="widowed">Widowed</MenuItem>
          </Select>
        </FormControl>
      </Grid>
      <Grid item xs={12} sm={6}>
        <FormControl fullWidth required>
          <InputLabel>Blood Group *</InputLabel>
          <Select
            value={formData.personalDetails.bloodGroup}
            onChange={(e) => updateFormData('personalDetails', 'bloodGroup', e.target.value)}
            label="Blood Group *"
          >
            {['A+', 'A-', 'B+', 'B-', 'AB+', 'AB-', 'O+', 'O-'].map(group => (
              <MenuItem key={group} value={group}>{group}</MenuItem>
            ))}
          </Select>
        </FormControl>
      </Grid>
      <Grid item xs={12} sm={6}>
        <TextField
          fullWidth
          label="Phone Number *"
          value={formData.personalDetails.phoneNumber}
          onChange={(e) => updateFormData('personalDetails', 'phoneNumber', e.target.value)}
          required
        />
      </Grid>
      <Grid item xs={12} sm={6}>
        <TextField
          fullWidth
          label="Email Address"
          type="email"
          value={formData.personalDetails.emailAddress}
          onChange={(e) => updateFormData('personalDetails', 'emailAddress', e.target.value)}
        />
      </Grid>

      {/* Education Details */}
      <Grid item xs={12}>
        <Typography variant="h6" gutterBottom>Education Details</Typography>
      </Grid>
      <Grid item xs={12} sm={6}>
        <FormControl fullWidth>
          <InputLabel>Highest Qualification</InputLabel>
          <Select
            value={formData.personalDetails.education.highestQualification}
            onChange={(e) => updateNestedFormData('personalDetails', 'education', 'highestQualification', e.target.value)}
            label="Highest Qualification"
          >
            <MenuItem value="below_10th">Below 10th</MenuItem>
            <MenuItem value="10th_pass">10th Pass</MenuItem>
            <MenuItem value="12th_pass">12th Pass</MenuItem>
            <MenuItem value="diploma">Diploma</MenuItem>
            <MenuItem value="graduate">Graduate</MenuItem>
            <MenuItem value="post_graduate">Post Graduate</MenuItem>
            <MenuItem value="professional">Professional</MenuItem>
          </Select>
        </FormControl>
      </Grid>
      <Grid item xs={12} sm={6}>
        <TextField
          fullWidth
          label="Institute Name"
          value={formData.personalDetails.education.instituteName}
          onChange={(e) => updateNestedFormData('personalDetails', 'education', 'instituteName', e.target.value)}
        />
      </Grid>
      <Grid item xs={12} sm={6}>
        <TextField
          fullWidth
          label="Year of Passing"
          type="number"
          value={formData.personalDetails.education.yearOfPassing}
          onChange={(e) => updateNestedFormData('personalDetails', 'education', 'yearOfPassing', e.target.value)}
        />
      </Grid>
      <Grid item xs={12} sm={6}>
        <TextField
          fullWidth
          label="Percentage/Grade"
          value={formData.personalDetails.education.percentage}
          onChange={(e) => updateNestedFormData('personalDetails', 'education', 'percentage', e.target.value)}
        />
      </Grid>

      {/* Physical Details */}
      <Grid item xs={12}>
        <Typography variant="h6" gutterBottom>Physical Details</Typography>
      </Grid>
      <Grid item xs={12} sm={6}>
        <TextField
          fullWidth
          label="Height (cm)"
          value={formData.personalDetails.physicalDetails.height}
          onChange={(e) => updateNestedFormData('personalDetails', 'physicalDetails', 'height', e.target.value)}
        />
      </Grid>
      <Grid item xs={12} sm={6}>
        <TextField
          fullWidth
          label="Weight (kg)"
          value={formData.personalDetails.physicalDetails.weight}
          onChange={(e) => updateNestedFormData('personalDetails', 'physicalDetails', 'weight', e.target.value)}
        />
      </Grid>

      {/* Emergency Contact */}
      <Grid item xs={12}>
        <Typography variant="h6" gutterBottom>Emergency Contact</Typography>
      </Grid>
      <Grid item xs={12} sm={6}>
        <TextField
          fullWidth
          label="Contact Name"
          value={formData.personalDetails.emergencyContact.name}
          onChange={(e) => updateNestedFormData('personalDetails', 'emergencyContact', 'name', e.target.value)}
        />
      </Grid>
      <Grid item xs={12} sm={6}>
        <TextField
          fullWidth
          label="Relationship"
          value={formData.personalDetails.emergencyContact.relationship}
          onChange={(e) => updateNestedFormData('personalDetails', 'emergencyContact', 'relationship', e.target.value)}
        />
      </Grid>
      <Grid item xs={12} sm={6}>
        <TextField
          fullWidth
          label="Contact Phone"
          value={formData.personalDetails.emergencyContact.phone}
          onChange={(e) => updateNestedFormData('personalDetails', 'emergencyContact', 'phone', e.target.value)}
        />
      </Grid>
    </Grid>
  );

  const renderIdentificationStep = () => (
    <Grid container spacing={3}>
      <Grid item xs={12} sm={6}>
        <TextField
          fullWidth
          label="PF Number *"
          value={formData.identificationDetails.pfNumber}
          onChange={(e) => updateFormData('identificationDetails', 'pfNumber', e.target.value)}
          required
          placeholder="XX/XXX/0000000/000/0000000"
        />
      </Grid>
      <Grid item xs={12} sm={6}>
        <TextField
          fullWidth
          label="ESIC Number *"
          value={formData.identificationDetails.esicNumber}
          onChange={(e) => updateFormData('identificationDetails', 'esicNumber', e.target.value)}
          required
          placeholder="0000000000"
        />
      </Grid>
      <Grid item xs={12} sm={6}>
        <TextField
          fullWidth
          label="PAN Number *"
          value={formData.identificationDetails.panNumber}
          onChange={(e) => updateFormData('identificationDetails', 'panNumber', e.target.value)}
          required
          placeholder="ABCDE1234F"
        />
      </Grid>
      <Grid item xs={12} sm={6}>
        <TextField
          fullWidth
          label="Aadhaar Number *"
          value={formData.identificationDetails.aadhaarNumber}
          onChange={(e) => updateFormData('identificationDetails', 'aadhaarNumber', e.target.value)}
          required
          placeholder="000000000000"
        />
      </Grid>

      <Grid item xs={12} sm={6}>
        <FormControl fullWidth>
          <InputLabel>Police Verification</InputLabel>
          <Select
            value={typeof formData.identificationDetails.policeVerification === 'boolean' ? (formData.identificationDetails.policeVerification ? 'true' : 'false') : (formData.identificationDetails.policeVerification || 'false')}
            onChange={(e) => updateFormData('identificationDetails', 'policeVerification', e.target.value === 'true')}
            label="Police Verification"
          >
            <MenuItem value="true">Yes</MenuItem>
            <MenuItem value="false">No</MenuItem>
          </Select>
        </FormControl>
      </Grid>

      <Grid item xs={12}>
        <Typography variant="h6" gutterBottom>Validity Details</Typography>
      </Grid>
      <Grid item xs={12} sm={4}>
        <DatePicker
          label="PF Valid Till *"
          value={formData.identificationDetails.validityDetails.pfValidTill ? 
                dayjs(formData.identificationDetails.validityDetails.pfValidTill) : null}
          onChange={(date) => updateNestedFormData('identificationDetails', 'validityDetails', 'pfValidTill', date?.toDate())}
          renderInput={(params) => <TextField {...params} fullWidth required />}
        />
      </Grid>
      <Grid item xs={12} sm={4}>
        <DatePicker
          label="ESIC Valid Till *"
          value={formData.identificationDetails.validityDetails.esicValidTill ? 
                dayjs(formData.identificationDetails.validityDetails.esicValidTill) : null}
          onChange={(date) => updateNestedFormData('identificationDetails', 'validityDetails', 'esicValidTill', date?.toDate())}
          renderInput={(params) => <TextField {...params} fullWidth required />}
        />
      </Grid>
      <Grid item xs={12} sm={4}>
        <DatePicker
          label="Medical Valid Till *"
          value={formData.identificationDetails.validityDetails.medicalValidTill ? 
                dayjs(formData.identificationDetails.validityDetails.medicalValidTill) : null}
          onChange={(date) => updateNestedFormData('identificationDetails', 'validityDetails', 'medicalValidTill', date?.toDate())}
          renderInput={(params) => <TextField {...params} fullWidth required />}
        />
      </Grid>
    </Grid>
  );

  const renderMedicalDetailsStep = () => (
    <Grid container spacing={3}>
      <Grid item xs={12} sm={6}>
        <DatePicker
          label="Pre-Medical Date *"
          value={formData.medicalDetails.preMedicalDate ? 
                dayjs(formData.medicalDetails.preMedicalDate) : null}
          onChange={(date) => updateFormData('medicalDetails', 'preMedicalDate', date?.toDate())}
          renderInput={(params) => <TextField {...params} fullWidth required />}
        />
      </Grid>
      <Grid item xs={12} sm={6}>
        <FormControl fullWidth>
          <InputLabel>Medical Status</InputLabel>
          <Select
            value={formData.medicalDetails.medicalStatus}
            onChange={(e) => updateFormData('medicalDetails', 'medicalStatus', e.target.value)}
            label="Medical Status"
          >
            <MenuItem value="pending">Pending</MenuItem>
            <MenuItem value="completed">Completed</MenuItem>
            <MenuItem value="failed">Failed</MenuItem>
            <MenuItem value="expired">Expired</MenuItem>
          </Select>
        </FormControl>
      </Grid>
      <Grid item xs={12}>
        <TextField
          fullWidth
          label="Allergies (comma separated)"
          value={formData.medicalDetails.allergies.join(', ')}
          onChange={(e) => updateFormData('medicalDetails', 'allergies', e.target.value.split(', ').filter(a => a.trim()))}
          multiline
          rows={2}
        />
      </Grid>
      <Grid item xs={12}>
        <TextField
          fullWidth
          label="Chronic Conditions (comma separated)"
          value={formData.medicalDetails.chronicConditions.join(', ')}
          onChange={(e) => updateFormData('medicalDetails', 'chronicConditions', e.target.value.split(', ').filter(c => c.trim()))}
          multiline
          rows={2}
        />
      </Grid>
    </Grid>
  );

  const renderEmploymentStep = () => (
    <Grid container spacing={3}>
      <Grid item xs={12} sm={6}>
        <FormControl fullWidth required>
          <InputLabel>Contractor *</InputLabel>
          <Select
            value={formData.employmentDetails.contractorId}
            onChange={(e) => updateFormData('employmentDetails', 'contractorId', e.target.value)}
            label="Contractor *"
          >
            {contractors.map(contractor => (
              <MenuItem key={contractor._id} value={contractor._id}>
                {contractor.companyName}
              </MenuItem>
            ))}
          </Select>
        </FormControl>
      </Grid>
      <Grid item xs={12} sm={6}>
        <TextField
          fullWidth
          label="Designation *"
          value={formData.employmentDetails.designation}
          onChange={(e) => updateFormData('employmentDetails', 'designation', e.target.value)}
          required
        />
      </Grid>
      <Grid item xs={12} sm={6}>
        <TextField
          fullWidth
          label="Department"
          value={formData.employmentDetails.department}
          onChange={(e) => updateFormData('employmentDetails', 'department', e.target.value)}
        />
      </Grid>
      <Grid item xs={12} sm={6}>
        <DatePicker
          label="Joining Date *"
          value={formData.employmentDetails.joiningDate ? 
                dayjs(formData.employmentDetails.joiningDate) : null}
          onChange={(date) => updateFormData('employmentDetails', 'joiningDate', date?.toDate())}
          renderInput={(params) => <TextField {...params} fullWidth required />}
        />
      </Grid>
      <Grid item xs={12} sm={6}>
        <FormControl fullWidth>
          <InputLabel>Employment Type</InputLabel>
          <Select
            value={formData.employmentDetails.employmentType}
            onChange={(e) => updateFormData('employmentDetails', 'employmentType', e.target.value)}
            label="Employment Type"
          >
            <MenuItem value="permanent">Permanent</MenuItem>
            <MenuItem value="contract">Contract</MenuItem>
            <MenuItem value="temporary">Temporary</MenuItem>
          </Select>
        </FormControl>
      </Grid>
      <Grid item xs={12} sm={6}>
        <DatePicker
          label="Contract End Date"
          value={formData.employmentDetails.contractEndDate ? 
                dayjs(formData.employmentDetails.contractEndDate) : null}
          onChange={(date) => updateFormData('employmentDetails', 'contractEndDate', date?.toDate())}
          renderInput={(params) => <TextField {...params} fullWidth />}
        />
      </Grid>
      <Grid item xs={12} sm={6}>
        <TextField
          fullWidth
          label="Work Location"
          value={formData.employmentDetails.workLocation}
          onChange={(e) => updateFormData('employmentDetails', 'workLocation', e.target.value)}
        />
      </Grid>
      <Grid item xs={12} sm={6}>
        <TextField
          fullWidth
          label="Experience (Years)"
          type="number"
          value={formData.employmentDetails.experienceYears}
          onChange={(e) => updateFormData('employmentDetails', 'experienceYears', e.target.value)}
        />
      </Grid>
      <Grid item xs={12} sm={3}>
        <TextField
          fullWidth
          label="Shift Start"
          placeholder="HH:MM"
          value={formData.employmentDetails.shiftTimings.start}
          onChange={(e) => updateNestedFormData('employmentDetails', 'shiftTimings', 'start', e.target.value)}
        />
      </Grid>
      <Grid item xs={12} sm={3}>
        <TextField
          fullWidth
          label="Shift End"
          placeholder="HH:MM"
          value={formData.employmentDetails.shiftTimings.end}
          onChange={(e) => updateNestedFormData('employmentDetails', 'shiftTimings', 'end', e.target.value)}
        />
      </Grid>
      <Grid item xs={12}>
        <TextField
          fullWidth
          label="Skills (comma separated)"
          value={formData.employmentDetails.skills.join(', ')}
          onChange={(e) => updateFormData('employmentDetails', 'skills', e.target.value.split(', ').filter(s => s.trim()))}
        />
      </Grid>

      {/* Employment Extra Fields */}
      <Grid item xs={12} sm={6}>
        <TextField
          fullWidth
          label="Photo Path"
          value={formData.employmentDetails.photoPath}
          onChange={(e) => updateFormData('employmentDetails', 'photoPath', e.target.value)}
        />
      </Grid>
      <Grid item xs={12} sm={6}>
        <TextField
          fullWidth
          label="Thumb Impression"
          value={formData.employmentDetails.thumbImpression}
          onChange={(e) => updateFormData('employmentDetails', 'thumbImpression', e.target.value)}
        />
      </Grid>
      <Grid item xs={12} sm={6}>
        <FormControl fullWidth>
          <InputLabel>Current Status</InputLabel>
          <Select
            value={formData.employmentDetails.currentStatus}
            onChange={(e) => updateFormData('employmentDetails', 'currentStatus', e.target.value)}
            label="Current Status"
          >
            <MenuItem value="active">Active</MenuItem>
            <MenuItem value="inactive">Inactive</MenuItem>
            <MenuItem value="terminated">Terminated</MenuItem>
            <MenuItem value="on_leave">On Leave</MenuItem>
          </Select>
        </FormControl>
      </Grid>
      <Grid item xs={12} sm={6}>
        <DatePicker
          label="Medical Fitness Date"
          value={formData.employmentDetails.medicalFitnessDate ? 
                dayjs(formData.employmentDetails.medicalFitnessDate) : null}
          onChange={(date) => updateFormData('employmentDetails', 'medicalFitnessDate', date?.toDate())}
          renderInput={(params) => <TextField {...params} fullWidth />}
        />
      </Grid>
      <Grid item xs={12} sm={6}>
        <TextField
          fullWidth
          label="Termination Reason"
          value={formData.employmentDetails.terminationReason}
          onChange={(e) => updateFormData('employmentDetails', 'terminationReason', e.target.value)}
        />
      </Grid>
      <Grid item xs={12} sm={6}>
        <DatePicker
          label="Termination Date"
          value={formData.employmentDetails.terminationDate ? 
                dayjs(formData.employmentDetails.terminationDate) : null}
          onChange={(date) => updateFormData('employmentDetails', 'terminationDate', date?.toDate())}
          renderInput={(params) => <TextField {...params} fullWidth />}
        />
      </Grid>

      {/* Salary Details */}
      <Grid item xs={12}>
        <Typography variant="h6" gutterBottom>Salary Details</Typography>
      </Grid>
      <Grid item xs={12} sm={6}>
        <TextField
          fullWidth
          label="Basic Salary"
          type="number"
          value={formData.employmentDetails.salary.basicSalary}
          onChange={(e) => updateNestedFormData('employmentDetails', 'salary', 'basicSalary', e.target.value)}
        />
      </Grid>
      <Grid item xs={12} sm={6}>
        <TextField
          fullWidth
          label="HRA"
          type="number"
          value={formData.employmentDetails.salary.hra}
          onChange={(e) => updateNestedFormData('employmentDetails', 'salary', 'hra', e.target.value)}
        />
      </Grid>
      <Grid item xs={12} sm={6}>
        <TextField
          fullWidth
          label="Conveyance"
          type="number"
          value={formData.employmentDetails.salary.conveyance}
          onChange={(e) => updateNestedFormData('employmentDetails', 'salary', 'conveyance', e.target.value)}
        />
      </Grid>
      <Grid item xs={12} sm={6}>
        <TextField
          fullWidth
          label="Medical Allowance"
          type="number"
          value={formData.employmentDetails.salary.medical}
          onChange={(e) => updateNestedFormData('employmentDetails', 'salary', 'medical', e.target.value)}
        />
      </Grid>
      <Grid item xs={12} sm={6}>
        <TextField
          fullWidth
          label="Others"
          type="number"
          value={formData.employmentDetails.salary.allowances.others}
          onChange={(e) => updateNestedFormData('employmentDetails', 'salary', 'allowances', { ...formData.employmentDetails.salary.allowances, others: e.target.value })}
        />
      </Grid>
    </Grid>
  );

  const renderBankingStep = () => (
    <Grid container spacing={3}>
      <Grid item xs={12}>
        <Typography variant="h6" gutterBottom>Primary Bank Account</Typography>
      </Grid>
      <Grid item xs={12} sm={6}>
        <TextField
          fullWidth
          label="Account Number *"
          value={formData.bankingDetails.primaryAccount.accountNumber}
          onChange={(e) => updateNestedFormData('bankingDetails', 'primaryAccount', 'accountNumber', e.target.value)}
          required
        />
      </Grid>
      <Grid item xs={12} sm={6}>
        <TextField
          fullWidth
          label="Account Holder Name *"
          value={formData.bankingDetails.primaryAccount.accountHolderName}
          onChange={(e) => updateNestedFormData('bankingDetails', 'primaryAccount', 'accountHolderName', e.target.value)}
          required
        />
      </Grid>
      <Grid item xs={12} sm={6}>
        <TextField
          fullWidth
          label="Bank Name *"
          value={formData.bankingDetails.primaryAccount.bankName}
          onChange={(e) => updateNestedFormData('bankingDetails', 'primaryAccount', 'bankName', e.target.value)}
          required
        />
      </Grid>
      <Grid item xs={12} sm={6}>
        <TextField
          fullWidth
          label="Branch Name"
          value={formData.bankingDetails.primaryAccount.branchName}
          onChange={(e) => updateNestedFormData('bankingDetails', 'primaryAccount', 'branchName', e.target.value)}
        />
      </Grid>
      <Grid item xs={12} sm={6}>
        <TextField
          fullWidth
          label="IFSC Code *"
          value={formData.bankingDetails.primaryAccount.ifscCode}
          onChange={(e) => updateNestedFormData('bankingDetails', 'primaryAccount', 'ifscCode', e.target.value)}
          required
          placeholder="ABCD0123456"
        />
      </Grid>
      <Grid item xs={12} sm={6}>
        <FormControl fullWidth>
          <InputLabel>Account Type</InputLabel>
          <Select
            value={formData.bankingDetails.primaryAccount.accountType}
            onChange={(e) => updateNestedFormData('bankingDetails', 'primaryAccount', 'accountType', e.target.value)}
            label="Account Type"
          >
            <MenuItem value="savings">Savings</MenuItem>
            <MenuItem value="current">Current</MenuItem>
          </Select>
        </FormControl>
      </Grid>

      <Grid item xs={12}>
        <Typography variant="h6" gutterBottom>Digital Payments</Typography>
      </Grid>
      <Grid item xs={12} sm={6}>
        <TextField
          fullWidth
          label="UPI ID"
          value={formData.bankingDetails.digitalPayments.upiId}
          onChange={(e) => updateNestedFormData('bankingDetails', 'digitalPayments', 'upiId', e.target.value)}
          placeholder="user@paytm"
        />
      </Grid>
      <Grid item xs={12} sm={6}>
        <TextField
          fullWidth
          label="Preferred UPI App"
          value={formData.bankingDetails.digitalPayments.preferredUpiApp}
          onChange={(e) => updateNestedFormData('bankingDetails', 'digitalPayments', 'preferredUpiApp', e.target.value)}
        />
      </Grid>

      {/* Nominee Section at Bottom */}
      <Grid item xs={12}>
        <Typography variant="h6" gutterBottom>Nominee</Typography>
      </Grid>
      <Grid item xs={12} sm={6}>
        <TextField
          fullWidth
          label="Nominee Name"
          value={formData.bankingDetails.nominee.name}
          onChange={(e) => updateNestedFormData('bankingDetails', 'nominee', 'name', e.target.value)}
        />
      </Grid>
      <Grid item xs={12} sm={6}>
        <TextField
          fullWidth
          label="Nominee Relationship"
          value={formData.bankingDetails.nominee.relationship}
          onChange={(e) => updateNestedFormData('bankingDetails', 'nominee', 'relationship', e.target.value)}
        />
      </Grid>
    </Grid>
  );

  const renderDocumentsStep = () => (
    <Grid container spacing={3}>
      <Grid item xs={12}>
        <Alert severity="info">
          Upload all required documents. Supported formats: JPG, PNG, PDF (Max size: 10MB)
        </Alert>
      </Grid>

      <Grid item xs={12} sm={6}>
        <Card>
          <CardContent>
            <Typography variant="h6" gutterBottom>Profile Photo</Typography>
            <input
              accept="image/*"
              style={{ display: 'none' }}
              id="profile-photo-upload"
              type="file"
              onChange={(e) => handleFileUpload('profilePhoto', e.target.files[0])}
            />
            <label htmlFor="profile-photo-upload">
              <IconButton color="primary" aria-label="upload picture" component="span">
                <PhotoCamera />
              </IconButton>
              <Button variant="outlined" component="span" startIcon={<UploadIcon />}>
                Upload Photo
              </Button>
            </label>
            {uploadFiles.profilePhoto && (
              <Chip 
                label={uploadFiles.profilePhoto.name} 
                color="success" 
                variant="outlined" 
                sx={{ mt: 1 }}
              />
            )}
          </CardContent>
        </Card>
      </Grid>

      <Grid item xs={12} sm={6}>
        <Card>
          <CardContent>
            <Typography variant="h6" gutterBottom>Aadhaar Card (Front)</Typography>
            <input
              accept="image/*,application/pdf"
              style={{ display: 'none' }}
              id="aadhaar-front-upload"
              type="file"
              onChange={(e) => handleFileUpload('aadhaarFront', e.target.files[0])}
            />
            <label htmlFor="aadhaar-front-upload">
              <Button variant="outlined" component="span" startIcon={<UploadIcon />}>
                Upload Aadhaar Front
              </Button>
            </label>
            {uploadFiles.aadhaarFront && (
              <Chip 
                label={uploadFiles.aadhaarFront.name} 
                color="success" 
                variant="outlined" 
                sx={{ mt: 1 }}
              />
            )}
          </CardContent>
        </Card>
      </Grid>

      <Grid item xs={12} sm={6}>
        <Card>
          <CardContent>
            <Typography variant="h6" gutterBottom>Aadhaar Card (Back)</Typography>
            <input
              accept="image/*,application/pdf"
              style={{ display: 'none' }}
              id="aadhaar-back-upload"
              type="file"
              onChange={(e) => handleFileUpload('aadhaarBack', e.target.files[0])}
            />
            <label htmlFor="aadhaar-back-upload">
              <Button variant="outlined" component="span" startIcon={<UploadIcon />}>
                Upload Aadhaar Back
              </Button>
            </label>
            {uploadFiles.aadhaarBack && (
              <Chip 
                label={uploadFiles.aadhaarBack.name} 
                color="success" 
                variant="outlined" 
                sx={{ mt: 1 }}
              />
            )}
          </CardContent>
        </Card>
      </Grid>

      <Grid item xs={12} sm={6}>
        <Card>
          <CardContent>
            <Typography variant="h6" gutterBottom>PAN Card</Typography>
            <input
              accept="image/*,application/pdf"
              style={{ display: 'none' }}
              id="pan-card-upload"
              type="file"
              onChange={(e) => handleFileUpload('panCard', e.target.files[0])}
            />
            <label htmlFor="pan-card-upload">
              <Button variant="outlined" component="span" startIcon={<UploadIcon />}>
                Upload PAN Card
              </Button>
            </label>
            {uploadFiles.panCard && (
              <Chip 
                label={uploadFiles.panCard.name} 
                color="success" 
                variant="outlined" 
                sx={{ mt: 1 }}
              />
            )}
          </CardContent>
        </Card>
      </Grid>

      <Grid item xs={12} sm={6}>
        <Card>
          <CardContent>
            <Typography variant="h6" gutterBottom>Education Certificate</Typography>
            <input
              accept="image/*,application/pdf"
              style={{ display: 'none' }}
              id="education-certificate-upload"
              type="file"
              onChange={(e) => handleFileUpload('educationCertificate', e.target.files[0])}
            />
            <label htmlFor="education-certificate-upload">
              <Button variant="outlined" component="span" startIcon={<UploadIcon />}>
                Upload Certificate
              </Button>
            </label>
            {uploadFiles.educationCertificate && (
              <Chip 
                label={uploadFiles.educationCertificate.name} 
                color="success" 
                variant="outlined" 
                sx={{ mt: 1 }}
              />
            )}
          </CardContent>
        </Card>
      </Grid>

      <Grid item xs={12} sm={6}>
        <Card>
          <CardContent>
            <Typography variant="h6" gutterBottom>Medical Certificate</Typography>
            <input
              accept="image/*,application/pdf"
              style={{ display: 'none' }}
              id="medical-certificate-upload"
              type="file"
              onChange={(e) => handleFileUpload('medicalCertificate', e.target.files[0])}
            />
            <label htmlFor="medical-certificate-upload">
              <Button variant="outlined" component="span" startIcon={<UploadIcon />}>
                Upload Medical Certificate
              </Button>
            </label>
            {uploadFiles.medicalCertificate && (
              <Chip 
                label={uploadFiles.medicalCertificate.name} 
                color="success" 
                variant="outlined" 
                sx={{ mt: 1 }}
              />
            )}
          </CardContent>
        </Card>
      </Grid>
    </Grid>
  );

  const renderStepContent = (step) => {
    switch (step) {
      case 0:
        return renderPersonalDetailsStep();
      case 1:
        return renderIdentificationStep();
      case 2:
        return renderMedicalDetailsStep();
      case 3:
        return renderEmploymentStep();
      case 4:
        return renderBankingStep();
      case 5:
        return renderDocumentsStep();
      default:
        return 'Unknown step';
    }
  };

  return (
    <LocalizationProvider dateAdapter={AdapterDayjs}>
      <Box sx={{ width: '100%' }}>
        <Stepper activeStep={activeStep} sx={{ mb: 4 }}>
          {steps.map((label, index) => (
            <Step key={label}>
              <StepLabel>{label}</StepLabel>
            </Step>
          ))}
        </Stepper>

        <Card>
          <CardContent>
            {renderStepContent(activeStep)}
          </CardContent>
        </Card>

        <Box sx={{ display: 'flex', flexDirection: 'row', pt: 2 }}>
          <Button
            color="inherit"
            disabled={activeStep === 0}
            onClick={handleBack}
            sx={{ mr: 1 }}
          >
            Back
          </Button>
          <Box sx={{ flex: '1 1 auto' }} />
          {activeStep === steps.length - 1 ? (
            <Button 
              onClick={handleSave} 
              disabled={loading}
              variant="contained"
            >
              {loading ? 'Saving...' : (workerId ? 'Update Worker' : 'Create Worker')}
            </Button>
          ) : (
            <Button onClick={handleNext} variant="contained">
              Next
            </Button>
          )}
          <Button onClick={onCancel} sx={{ ml: 1 }}>
            Cancel
          </Button>
        </Box>
      </Box>
    </LocalizationProvider>
  );
};

export default WorkerForm;